import type { ChangeEvent } from 'react';
import { useEffect, lazy, Suspense, useMemo, useState } from 'react';

import StringsPropVerification from '@web-platform/global-components-higher-order-components/StringsPropVerification';
import { trackEventFn as defaultTrackEventFn } from '@web-platform/global-components-utils/tracking';
import { GLOBAL } from 'saddlebag-browser';
import observer from 'saddlebag-observer';

import FeatureImage from './components/FeatureImage';
import LoadingSpinner from './components/LoadingSpinner';
import ModalContainer from './components/ModalContainer';
import { EVENT_NAMES, PAGE_FLOW, stringsKeys } from './constants';
import { ACTIONS, COMPONENTS } from './constants/tracking';
import { LoginContext } from './context/loginContext';
import { getConsentData } from './utils/consentUtils';
import decorateLogger from './utils/decorateLogger';
import { getSessionType } from './utils/sessionTypeState';
import useActionId from './utils/useActionId';
import useSubscriptionStatus from './utils/useSubscriptionStatus';
import { loginWebTrackingEvent } from './utils/userTrackingEvents';

import type { BundledConsentVariant, LoginOnClose, Nullable, PageFlow, Strings } from './types'; // NOSONAR: BundledConsentVariant import needed for the deprecated LoginFormProps member below, kept only for backwards compatibility ahead of a major release
import type { PartialAuthClient } from './types/auth-client';
import type { ConsentLabel } from './utils/consentUtils';
import type { Source } from '@web-platform/global-components-marketing-consent';
import type { SubscriptionClientInterface } from '@web-platform/global-components-marketing-consent/types';
import type { Logger } from '@web-platform/global-components-types/utils';
import type { TrackingEventFn } from '@web-platform/global-components-utils/tracking';

const IntroLazy = lazy(
  () =>
    import(
      /* webpackChunkName: "LoginFormGlobalComponentIntro" */
      './components/Intro'
    ),
);

const LoginSuccessLazy = lazy(
  () =>
    import(
      /* webpackChunkName: "LoginFormGlobalComponentLoginSuccess" */
      './components/LoginSuccess'
    ),
);

const OneTimePasswordLazy = lazy(
  () =>
    import(
      /* webpackChunkName: "LoginFormGlobalComponentOneTimePassword" */
      './components/OneTimePassword'
    ),
);

const RegulatoryConsentLazy = lazy(
  () =>
    import(
      /* webpackChunkName: "LoginFormGlobalComponentRegulatoryConsent" */
      './components/RegulatoryConsent'
    ),
);

const MarketingConsentLazy = lazy(
  () =>
    import(
      /* webpackChunkName: "LoginFormGlobalComponentMarketingConsent" */
      './components/MarketingConsent'
    ),
);

/** These props are required for using Login Form
 * @interface
 * @public
 */
export interface LoginFormProps {
  /**
   * A UUID used for tracking events
   */
  actionId?: Nullable<string>;
  /**
   * An instance of `@skyscanner/auth-client`. Used for authenticating the traveller.
   */
  authClient: PartialAuthClient;
  /**
   * TODO: Replace this as part of SRS-722 https://skyscanner.atlassian.net/browse/SRS-722
   * Used to control which page the traveller lands on when the login form is opened
   */
  entryPage?: PageFlow;
  /**
   * Used to decorate all tracking events and logger events emitted by the component to monitor the performance of
   * experiments related to travellers' logging in.
   *
   * For tracking events, will be logged to the `experiment_context` field in the LoginWebEvent schema
   * For monitoring events, will be logged to the `experimentContext` field
   */
  experimentContext?: string;
  /**
   * Used to control the visibility of the login form.
   */
  isOpen: boolean;
  /**
   * Indicates whether the environment is production or not. Used to determine if the `StringsPropVerification` wrapper should render error states in the UI.
   */
  isProductionEnv: boolean;
  /**
   * Allow reauthentication from the login form.
   */
  isReauthentication?: boolean;
  /**
   * Indicates whether the login form should be rendered as a modal or as a page.
   */
  isModal?: boolean;
  /**
   * The locale of the traveller. Used for observability.
   */
  locale: string;
  /**
   * Object for logging operational and warning events related to the component.
   */
  logger: Logger;
  /**
   * The market of the traveller. Used to show altered UX in South Korea & China.
   */
  market: string;
  /**
   * Function called when the login form is closed.
   * @param isLoggedIn - the logged in status of the traveller
   */
  onClose: ({ isLoggedIn }: LoginOnClose) => void;
  /**
   * The current view that a traveller is on, microsite specific. Used for logging/tracking purposes.
   */
  screenName?: string;
  /**
   * Captures the mechanism through which the login form was opened (i.e. Save to list). Used for logging/tracking purposes.
   */
  source: Source;
  /**
   * Object containing translations for the component.
   */
  strings: Strings;
  /**
   * Provide implementation for the subscription methods for marketing consent page
   */
  subscriptionClient: SubscriptionClientInterface;
  /**
   * Function to track events. If not provided, `defaultTrackEventFn` will be used.
   */
  trackEventFn?: TrackingEventFn;
  /**
   * Captures which microsite is responsible for opening the login form. This is used for logging/tracking purposes.
   */
  consumer: string;
  /**
   * @deprecated Superseded by {@link LoginFormProps.enableLoginFormBundledConsent} (GATE-1396).
   * No longer read by this component — retained only to avoid a breaking API change ahead of
   * the next major release, at which point it will be removed. Passing this prop has no effect.
   *
   * @defaultValue undefined
   */
  // eslint-disable-next-line react/no-unused-prop-types -- intentionally unused, see @deprecated above
  unstable__enableLoginFormBundledConsent?: BundledConsentVariant; // NOSONAR: deprecated, kept only for backwards compatibility ahead of a major release
  /**
   * Feature flag for the bundled marketing-consent flow. When `true`, the standalone
   * marketing-consent step is skipped, the traveller is automatically opted in to marketing
   * communications, and the success step shows copy such as "You're logged in and signed
   * up!". When omitted or `false`, the standalone marketing-consent step is shown as normal.
   *
   * @defaultValue undefined
   */
  enableLoginFormBundledConsent?: boolean;
}

const $window = GLOBAL.getWindow();

/**
 * The `LoginForm` component provides a common UX to enable travellers to log in to Skyscanner. It
 * allows the traveller to log in using either a social login method (specifically; Apple, Facebook,
 * Google) or directly with their email.
 *
 * @remarks
 * Due to restrictions in certain markets, there are some slight variances:
 * - In China, only email and Apple social logins are provided
 * - In South Korea, an extra consent screen is displayed
 *
 * @example
 * ```tsx
 * import LoginForm, { SOURCES } from '@skyscanner-internal/global-component/login-form';
 * import type { LoginOnClose } from '@skyscanner-internal/global-component/login-form';
 * import { SubscriptionClient } from '@skyscanner-internal/global-component/login-form/helpers';
 * import AuthClient from '@skyscanner/auth-client'
 * ...
 * const Consumer = () => {
 *   const [isOpen, setIsOpen] = useState(false);
 *   const [loggedIn, setLoggedIn] = useState(false);
 *
 *   const onClose = ({ isLoggedIn }: LoginOnClose) => {
 *     setIsOpen(false);
 *     isLoggedIn !== loggedIn && setLoggedIn(isLoggedIn);
 *   };
 *
 *   const authClient = new AuthClient(authClientConfig);
 *   const subscriptionClient = new SubscriptionClient();
 *
 *   return (
 *     <LoginForm
 *       authClient={authClient}
 *       isOpen={isOpen}
 *       isProductionEnv={false}
 *       locale="en-GB"
 *       logger={logger}
 *       market="UK"
 *       onClose={onClose}
 *       strings={strings}
 *       subscriptionClient={subscriptionClient}
 *       source={SOURCES.S2L_HEADER}
 *       consumer="falcon"
 *       {...props}
 *     />
 *   );
 * };
 * ```
 */
/**
 * @public
 */
const LoginForm = ({
  actionId = null,
  authClient,
  consumer,
  enableLoginFormBundledConsent = false,
  entryPage = PAGE_FLOW.INTRO,
  experimentContext,
  isModal = true,
  isOpen,
  isProductionEnv = false,
  isReauthentication = false,
  locale,
  logger,
  market,
  onClose,
  screenName,
  source,
  strings,
  subscriptionClient,
  trackEventFn = defaultTrackEventFn,
}: LoginFormProps) => {
  const [emailInput, setEmailInput] = useState('');
  const [pageFlow, setPageFlow] = useState<PageFlow>(entryPage);
  const [newUser, setNewUser] = useState<boolean>(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loginMethodCallback, setLoginMethodCallback] = useState<
    (() => Promise<boolean> | void) | undefined
  >();

  const [prevEntryPage, setPrevEntryPage] = useState(entryPage);
  if (prevEntryPage !== entryPage) {
    setPrevEntryPage(entryPage);
    setPageFlow(entryPage);
  }

  const resolvedActionId = useActionId(actionId, isOpen, setPageFlow);

  const consentData: Nullable<{ labels: ConsentLabel[]; strings: string[] }> = getConsentData(
    market,
    strings,
    logger,
  );
  const decoratedLogger = decorateLogger({
    experimentContext,
    locale,
    logger,
    loginStep: pageFlow,
    market,
    regulatoryConsentRequired: String(!!consentData),
    screenName: String(screenName),
    source: String(source),
  });

  const loginContextProviderValue = useMemo(
    () => ({
      isNewUser: newUser,
      isLoggedIn,
      pageFlow,
      updateIsLoggedIn: setIsLoggedIn,
      updateIsNewUser: setNewUser,
      updatePageFlow: setPageFlow,
    }),
    [isLoggedIn, newUser, pageFlow],
  );

  const handleEmailChange = (event: ChangeEvent<HTMLInputElement>) => {
    const providedEmail = event.target.value;
    setEmailInput(providedEmail);
  };

  const handleBack = () => {
    if (pageFlow === PAGE_FLOW.INTRO) {
      $window.history.back();
    } else {
      setPageFlow(PAGE_FLOW.INTRO);
    }
  };

  const handleClose = () => {
    /*
     * A number of microsites rely on this event to know when the login modal has been closed. This
     * is largely a hangover from the time of open components, and the use of this should be reviewed
     * in the future.
     *
     * For now, this is required to enable features such as save-to-list and price alerts.
     */
    observer.publish('sid-login-modal-closed');

    decoratedLogger.logOperationalEvent({
      eventName: EVENT_NAMES.LOGIN_CLOSED,
    });

    trackEventFn(
      loginWebTrackingEvent({
        action: ACTIONS.LOGIN_DISMISS,
        action_id: resolvedActionId,
        component: COMPONENTS.LOGIN_FORM_GC,
        experiment_context: experimentContext,
        microsite: consumer,
        screen_name: screenName,
        source,
        step_name: pageFlow,
      }),
    );

    onClose({ isLoggedIn });
  };

  const shouldShowBackButton =
    pageFlow === PAGE_FLOW.OTP ||
    pageFlow === PAGE_FLOW.REGULATORY_CONSENT ||
    (pageFlow === PAGE_FLOW.INTRO && !isModal);

  const { checkSubscriptionStatus, updateSubscription } = useSubscriptionStatus({
    handleClose,
    locale,
    logger: decoratedLogger,
    market,
    updatePageFlow: setPageFlow,
    subscriptionClient,
  });

  useEffect(() => {
    /*
     * A number of microsites rely on this event to know when the login modal has been opened. This
     * is largely a hangover from the time of open components, and the use of this should be reviewed
     * in the future.
     */
    if (isOpen) {
      observer.publish('sid-login-modal-open');

      logger &&
        decoratedLogger.logOperationalEvent({
          eventName: EVENT_NAMES.LOGIN_OPENED,
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const onConsentGiven = async (): Promise<boolean> => {
    consentData
      ? authClient.acceptLoginConsents(consentData.strings)
      : decoratedLogger.logOperationalEvent({
          eventName: EVENT_NAMES.CONSENT_MODAL_ACCEPT_CONSENT_FAILED,
        });
    if (loginMethodCallback) {
      return (await loginMethodCallback()) === true;
    }
    return false;
  };

  const getLoginIntroComponent = () => (
    <IntroLazy
      {...{
        actionId: resolvedActionId,
        authClient,
        isConsentRequired: !!consentData,
        emailInput,
        experimentContext,
        isProductionEnv,
        locale,
        logger: decoratedLogger,
        market,
        screenName,
        setLoginMethodCallback,
        strings,
        trackEventFn,
        consumer,
        source,
        onEmailChange: handleEmailChange,
      }}
    />
  );

  const getLoginSuccessComponent = () => (
    <LoginSuccessLazy
      actionId={resolvedActionId}
      checkSubscriptionStatus={checkSubscriptionStatus}
      experimentContext={experimentContext}
      market={market}
      screenName={screenName}
      strings={strings}
      trackEventFn={trackEventFn}
      consumer={consumer}
      source={source}
      isBundledConsentActive={enableLoginFormBundledConsent}
      logger={decoratedLogger}
      updateSubscription={updateSubscription}
    />
  );

  const getMarketingConsentComponent = () => (
    <MarketingConsentLazy
      actionId={resolvedActionId}
      consumer={consumer}
      experimentContext={experimentContext}
      logger={decoratedLogger}
      market={market}
      onComplete={updateSubscription}
      screenName={screenName}
      source={source}
      strings={strings}
      trackEventFn={trackEventFn}
    />
  );

  const getOneTimePasswordComponent = () => (
    <OneTimePasswordLazy
      email={emailInput}
      persistent={getSessionType()}
      {...{
        actionId: resolvedActionId,
        authClient,
        consumer,
        experimentContext,
        isProductionEnv,
        isReauthentication,
        locale,
        market,
        screenName,
        source,
        strings,
        trackEventFn,
      }}
    />
  );

  const getRegulatoryConsentComponent = () => (
    <RegulatoryConsentLazy
      {...{
        onSubmit: onConsentGiven,
        strings,
        actionId: resolvedActionId,
        consentLabels: consentData?.labels || [],
        experimentContext,
        logger: decoratedLogger,
        market,
        trackEventFn,
        screenName,
        consumer,
        source,
      }}
    />
  );

  const modalRouter = () =>
    ({
      [PAGE_FLOW.INTRO]: getLoginIntroComponent(),
      [PAGE_FLOW.OTP]: getOneTimePasswordComponent(),
      [PAGE_FLOW.LOGIN_SUCCESS]: getLoginSuccessComponent(),
      [PAGE_FLOW.MARKETING_CONSENT]: getMarketingConsentComponent(),
      [PAGE_FLOW.REGULATORY_CONSENT]: getRegulatoryConsentComponent(),
    })[pageFlow];

  return (
    <StringsPropVerification
      stringsKeys={stringsKeys}
      componentName={LoginForm.name}
      strings={strings}
      logger={logger}
      isProductionEnv={isProductionEnv}
    >
      <ModalContainer
        ariaLabel={
          pageFlow === PAGE_FLOW.REGULATORY_CONSENT
            ? strings.regulatoryConsentModalAriaLabel
            : strings.introModalAriaLabel
        }
        ariaLabelBack={
          pageFlow === PAGE_FLOW.REGULATORY_CONSENT
            ? strings.regulatoryConsentModalAriaLabelBack
            : strings.ariaLabelBack
        }
        ariaLabelClose={strings.ariaLabelClose}
        closeOnScrimClick={isModal}
        handleBack={handleBack}
        handleClose={handleClose}
        isOpen={isOpen}
        shouldShowBack={shouldShowBackButton}
        shouldShowClose={isModal}
      >
        <ModalContainer.PrimaryContent>
          <LoginContext.Provider value={loginContextProviderValue}>
            <Suspense fallback={LoadingSpinner()}>{modalRouter()}</Suspense>
          </LoginContext.Provider>
        </ModalContainer.PrimaryContent>
        <ModalContainer.SecondaryContent>
          <FeatureImage source={source} step={pageFlow} />
        </ModalContainer.SecondaryContent>
      </ModalContainer>
    </StringsPropVerification>
  );
};

export default LoginForm;
